/**
 * Stats — platform-wide numbers, visible only to the Shieldz admin account.
 *
 * Reads /api/admin/platform-stats (backend hard-gates to is_platform_admin).
 * Shows headline counts (users, merchants, invoices, volume, keyless/agent
 * accounts, API keys) plus a per-merchant table.
 */

import { Fragment, useCallback, useEffect, useState } from "react";
import {
  getPlatformStats,
  getPlatformMerchantInvoices,
  type PlatformStats,
  type PlatformMerchantInvoice,
  ApiError,
} from "../lib/api";
import { Banner, Btn } from "../components/ui";

const fmt = (n: number) => n.toLocaleString("en-US");

const STATUS_COLOR: Record<string, string> = {
  paid: "var(--ok, #22c07a)",
  pending: "var(--warn, #d7a021)",
  expired: "var(--ink-3)",
  failed: "var(--fail, #e0564f)",
};

export function Stats() {
  const [data, setData] = useState<PlatformStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState<number | null>(null);
  const [detail, setDetail] = useState<Record<number, PlatformMerchantInvoice[] | "loading" | "error">>({});

  const toggle = useCallback((id: number) => {
    setOpenId((cur) => (cur === id ? null : id));
    setDetail((d) => {
      if (d[id]) return d;
      void getPlatformMerchantInvoices(id)
        .then((r) => setDetail((prev) => ({ ...prev, [id]: r.invoices })))
        .catch(() => setDetail((prev) => ({ ...prev, [id]: "error" })));
      return { ...d, [id]: "loading" };
    });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await getPlatformStats());
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) {
        setError("Platform stats are only visible to the Shieldz admin account.");
      } else {
        setError(e instanceof Error ? e.message : "Could not load stats.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const cards: Array<{ label: string; value: string; hint?: string }> = data
    ? [
        { label: "Registered users", value: fmt(data.total_users) },
        { label: "Merchants", value: fmt(data.total_merchants) },
        { label: "Invoices", value: fmt(data.total_invoices), hint: `${fmt(data.paid_invoices)} paid · ${fmt(data.pending_invoices)} pending` },
        { label: "Volume (paid)", value: `$${data.total_volume_usd}` },
        { label: "Demo attempts", value: fmt(data.demo_attempts), hint: "all-time, survives demo purge" },
        { label: "Keyless accounts", value: fmt(data.keyless_accounts), hint: `${fmt(data.claimed_accounts)} claimed · agent/MCP links` },
        { label: "Payment pages", value: fmt(data.total_pages) },
        { label: "Active API keys", value: fmt(data.active_api_keys) },
      ]
    : [];

  return (
    <div className="pad" style={{ maxWidth: 980 }}>
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
        <p className="muted" style={{ margin: 0 }}>Platform-wide numbers. Admin only.</p>
        <Btn variant="secondary" onClick={() => void load()} disabled={loading}>
          {loading ? "Refreshing…" : "Refresh"}
        </Btn>
      </div>

      {error && <Banner tone="fail">{error}</Banner>}

      {data && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 14, marginBottom: 30 }}>
            {cards.map((c) => (
              <div key={c.label} style={{ border: "1px solid var(--line)", borderRadius: 14, padding: "16px 18px", background: "var(--surface)" }}>
                <div style={{ fontSize: 12.5, color: "var(--ink-3)", marginBottom: 8 }}>{c.label}</div>
                <div style={{ fontSize: 26, fontWeight: 700, letterSpacing: "-0.02em" }}>{c.value}</div>
                {c.hint && <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 6 }}>{c.hint}</div>}
              </div>
            ))}
          </div>

          {data.signups_daily && data.signups_daily.length > 0 && (
            <SignupsChart data={data.signups_daily} />
          )}

          {data.checkout_errors_24h && data.checkout_errors_24h.length > 0 && (
            <CheckoutErrorsTable rows={data.checkout_errors_24h} />
          )}

          <h2 style={{ fontSize: 17, margin: "0 0 12px" }}>Merchants</h2>
          <div style={{ border: "1px solid var(--line)", borderRadius: 14, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }}>
              <thead>
                <tr style={{ color: "var(--ink-3)", textAlign: "left" }}>
                  <th style={{ padding: "11px 14px", fontWeight: 600 }}>Merchant</th>
                  <th style={{ padding: "11px 14px", fontWeight: 600 }}>Invoices</th>
                  <th style={{ padding: "11px 14px", fontWeight: 600 }}>Paid</th>
                  <th style={{ padding: "11px 14px", fontWeight: 600 }}>Volume</th>
                  <th style={{ padding: "11px 14px", fontWeight: 600 }}>Joined</th>
                </tr>
              </thead>
              <tbody>
                {data.merchants.map((m) => {
                  const open = openId === m.id;
                  const rows = detail[m.id];
                  return (
                    <Fragment key={m.id}>
                      <tr
                        onClick={() => toggle(m.id)}
                        style={{ borderTop: "1px solid var(--line)", cursor: "pointer", background: open ? "var(--surface-2, rgba(127,127,127,0.06))" : undefined }}
                      >
                        <td style={{ padding: "11px 14px" }}>
                          <span style={{ display: "inline-block", width: 14, color: "var(--ink-3)", fontSize: 11 }}>{open ? "▾" : "▸"}</span>
                          {m.display_name || "Merchant"}
                        </td>
                        <td style={{ padding: "11px 14px" }}>{fmt(m.invoice_count)}</td>
                        <td style={{ padding: "11px 14px" }}>{fmt(m.paid_count)}</td>
                        <td style={{ padding: "11px 14px" }}>${m.volume_usd}</td>
                        <td style={{ padding: "11px 14px", color: "var(--ink-3)" }}>
                          {new Date(m.created_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}
                        </td>
                      </tr>
                      {open && (
                        <tr key={`${m.id}-detail`}>
                          <td colSpan={5} style={{ padding: 0, borderTop: "1px solid var(--line)" }}>
                            {rows === "loading" && (
                              <div style={{ padding: "14px 14px 14px 42px", color: "var(--ink-3)", fontSize: 13 }}>Loading invoices…</div>
                            )}
                            {rows === "error" && (
                              <div style={{ padding: "14px 14px 14px 42px", color: "var(--fail, #e0564f)", fontSize: 13 }}>Could not load invoices.</div>
                            )}
                            {Array.isArray(rows) && rows.length === 0 && (
                              <div style={{ padding: "14px 14px 14px 42px", color: "var(--ink-3)", fontSize: 13 }}>No invoices.</div>
                            )}
                            {Array.isArray(rows) && rows.length > 0 && (
                              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
                                <thead>
                                  <tr style={{ color: "var(--ink-3)", textAlign: "left" }}>
                                    <th style={{ padding: "8px 14px 8px 42px", fontWeight: 600 }}>Created</th>
                                    <th style={{ padding: "8px 10px", fontWeight: 600 }}>$</th>
                                    <th style={{ padding: "8px 10px", fontWeight: 600 }}>Memo</th>
                                    <th style={{ padding: "8px 10px", fontWeight: 600 }}>Status</th>
                                    <th style={{ padding: "8px 10px", fontWeight: 600 }}>Rail</th>
                                    <th style={{ padding: "8px 10px", fontWeight: 600 }}>Source</th>
                                    <th style={{ padding: "8px 14px 8px 10px", fontWeight: 600 }}>Email</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {rows.map((inv) => (
                                    <tr key={inv.public_id} style={{ borderTop: "1px solid var(--line)" }}>
                                      <td style={{ padding: "8px 14px 8px 42px", color: "var(--ink-3)", whiteSpace: "nowrap" }}>
                                        {new Date(inv.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                                      </td>
                                      <td style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>${inv.amount_usd}</td>
                                      <td style={{ padding: "8px 10px", maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={inv.memo ?? ""}>
                                        {inv.memo || <span style={{ color: "var(--ink-3)" }}>-</span>}
                                      </td>
                                      <td style={{ padding: "8px 10px", color: STATUS_COLOR[inv.status] ?? "inherit" }}>
                                        {inv.status}{inv.mode === "test" ? " (test)" : ""}
                                      </td>
                                      <td style={{ padding: "8px 10px", color: "var(--ink-3)", whiteSpace: "nowrap" }}>
                                        {inv.settlement_chain}/{inv.settlement_asset.replace(`${inv.settlement_chain}.`, "")}
                                      </td>
                                      <td style={{ padding: "8px 10px", color: "var(--ink-3)" }}>{inv.source ?? "api"}</td>
                                      <td style={{ padding: "8px 14px 8px 10px", color: "var(--ink-3)", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                        {inv.customer_email || "-"}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
                {data.merchants.length === 0 && (
                  <tr><td colSpan={5} style={{ padding: "16px 14px", color: "var(--ink-3)" }}>No merchants yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

/** Last-7-days signups bar chart (merchant + keyless), inline SVG, no deps.
 *  Grouped bars per day; a small legend and per-day totals below. */
function SignupsChart({
  data,
}: {
  data: Array<{ date: string; merchants: number; keyless: number }>;
}) {
  const W = 620;
  const H = 200;
  const padL = 28;
  const padB = 34;
  const padT = 12;
  const innerW = W - padL - 8;
  const innerH = H - padB - padT;
  const max = Math.max(1, ...data.map((d) => d.merchants + d.keyless));
  const slot = innerW / data.length;
  const barW = Math.min(26, slot * 0.44);
  const gap = 3;
  const y = (v: number) => padT + innerH - (v / max) * innerH;
  const MERCH = "#16a34a";
  const KEYLESS = "#8b5cf6";
  const totalMerch = data.reduce((s, d) => s + d.merchants, 0);
  const totalKeyless = data.reduce((s, d) => s + d.keyless, 0);
  // Gridlines at 0, mid, max.
  const ticks = [0, Math.round(max / 2), max];

  const dow = (iso: string) => {
    const p = iso.split("-");
    const yy = Number(p[0]);
    const mm = Number(p[1]);
    const dd = Number(p[2]);
    return new Date(Date.UTC(yy, mm - 1, dd)).toLocaleDateString("en-US", {
      weekday: "short",
      timeZone: "UTC",
    });
  };

  return (
    <div style={{ marginBottom: 30 }}>
      <div className="row" style={{ justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
        <h2 style={{ fontSize: 17, margin: 0 }}>Signups, last 7 days</h2>
        <div style={{ display: "flex", gap: 16, fontSize: 12.5, color: "var(--ink-3)" }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <i style={{ width: 10, height: 10, borderRadius: 3, background: MERCH, display: "inline-block" }} />
            Merchant · {fmt(totalMerch)}
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <i style={{ width: 10, height: 10, borderRadius: 3, background: KEYLESS, display: "inline-block" }} />
            Keyless · {fmt(totalKeyless)}
          </span>
        </div>
      </div>
      <div style={{ border: "1px solid var(--line)", borderRadius: 14, padding: "14px 16px 8px", background: "var(--surface)" }}>
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="Signups per day for the last 7 days" style={{ display: "block" }}>
          {ticks.map((tk) => (
            <g key={tk}>
              <line x1={padL} y1={y(tk)} x2={W - 8} y2={y(tk)} stroke="var(--line)" strokeWidth={1} />
              <text x={padL - 6} y={y(tk) + 3.5} textAnchor="end" fontSize={10} fill="var(--ink-3)">{tk}</text>
            </g>
          ))}
          {data.map((d, i) => {
            const cx = padL + i * slot + slot / 2;
            const mx = cx - barW - gap / 2;
            const kx = cx + gap / 2;
            const total = d.merchants + d.keyless;
            return (
              <g key={d.date}>
                {d.merchants > 0 && (
                  <rect x={mx} y={y(d.merchants)} width={barW} height={padT + innerH - y(d.merchants)} rx={3} fill={MERCH} />
                )}
                {d.keyless > 0 && (
                  <rect x={kx} y={y(d.keyless)} width={barW} height={padT + innerH - y(d.keyless)} rx={3} fill={KEYLESS} />
                )}
                <text x={cx} y={H - padB + 15} textAnchor="middle" fontSize={10.5} fill="var(--ink-3)">{dow(d.date)}</text>
                <text x={cx} y={H - padB + 28} textAnchor="middle" fontSize={10} fill="var(--ink-3)" opacity={0.7}>{d.date.slice(5)}</text>
                {total > 0 && (
                  <text x={cx} y={y(Math.max(d.merchants, d.keyless)) - 5} textAnchor="middle" fontSize={10.5} fontWeight={600} fill="var(--ink-2, #444)">{total}</text>
                )}
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
}

/** Checkout-side quote/deposit failures in the last 24h, grouped by cause.
 *  Plain table, no chart: this is meant for scanning "what broke and how
 *  often", not trending over time. */
function CheckoutErrorsTable({
  rows,
}: {
  rows: Array<{ event: string; asset_id: string | null; protocol: string | null; reason: string | null; n: number }>;
}) {
  const EVENT_LABEL: Record<string, string> = {
    quote_failed: "No route (quote)",
    deposit_failed: "Deposit-open failed",
  };
  return (
    <div style={{ marginBottom: 30 }}>
      <h2 style={{ fontSize: 17, margin: "0 0 12px" }}>Checkout errors, last 24h</h2>
      <div style={{ border: "1px solid var(--line)", borderRadius: 14, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }}>
          <thead>
            <tr style={{ color: "var(--ink-3)", textAlign: "left" }}>
              <th style={{ padding: "11px 14px", fontWeight: 600 }}>Type</th>
              <th style={{ padding: "11px 14px", fontWeight: 600 }}>Asset</th>
              <th style={{ padding: "11px 14px", fontWeight: 600 }}>Protocol</th>
              <th style={{ padding: "11px 14px", fontWeight: 600 }}>Reason</th>
              <th style={{ padding: "11px 14px", fontWeight: 600 }}>Count</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} style={{ borderTop: "1px solid var(--line)" }}>
                <td style={{ padding: "10px 14px" }}>{EVENT_LABEL[r.event] ?? r.event}</td>
                <td style={{ padding: "10px 14px" }}>{r.asset_id ?? "-"}</td>
                <td style={{ padding: "10px 14px" }}>{r.protocol ?? "-"}</td>
                <td style={{ padding: "10px 14px", color: "var(--ink-3)" }}>{r.reason ?? "-"}</td>
                <td style={{ padding: "10px 14px", fontWeight: 600 }}>{fmt(r.n)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
